import type { ExtensionInstance, ExtensionType } from '@mangovoice/types'

/**
 * Line-specific fields returned by the API for type=Line.
 * PATCH accepts assigned_user_id, location_id, outbound_extension_id,
 * voicemail + notification, advanced + find-me settings.
 */
export interface LineSeedFields {
  assigned_user?: { id: number; name: string }
  assigned_profile?: { id: number; name: string }
  location?: { id: number; name: string }
  is_active?: number
  /** Outbound extension (another line) for dialing. */
  outbound_extension_id?: number | null
  /** Legacy OutDial extension flag: 0=No, 1=Yes. */
  outbound_extension?: number | null
  /** Legacy local area code used when OutDial is disabled. */
  outbound_local_area_code?: string | null
  /** Voicemail: time zone (IANA). */
  voicemail_time_zone?: string | null
  voicemail_password?: string | null
  voicemail_greeting_type?: string | null
  /** Outgoing greeting: use system default vs. a custom sound clip. */
  use_system_greetings?: boolean
  outgoing_message_unavailable_id?: number | null
  /** Email notifications: send, attach message, delete after send, address. */
  send_email_notifications?: boolean
  attach_voicemail_message?: boolean
  delete_message_after_send?: boolean
  message_email_address?: string | null
  /** Advanced: directory, caller ID, presence. */
  omit_from_directory?: boolean
  block_caller_id?: boolean
  use_local_presence?: boolean
  /** Find-me: mode, delay (seconds), cellphone. */
  findme_mode?: string | null
  findme_delay?: number | null
  findme_cellphone?: string | null
  /** Forwarding number. */
  forwarding_number?: string | null
  /** Advanced devices/codec. */
  preferred_codec?: string | null
  t38_device?: number | null
  call_recording?: number | boolean | null
  in_call_star_codes_enabled?: boolean
}

/**
 * Ring group-specific fields. PATCH handles name/number/omit;
 * PUT ring-group handles ring_strategy, custom_ringback_audio_id, ring_group_members.
 */
export interface RingGroupSeedFields {
  omit_from_directory?: boolean
  ring_strategy?: string | null
  custom_ringback_audio_id?: number | null
  /** Member lines for display; stored as extension ids. */
  members?: { id: number; number: string; name: string }[]
}

/**
 * Conference room-specific fields. PATCH handles name/number/omit;
 * PUT conference-room handles moderator, pins, muted, announcements, playlist.
 */
export interface ConferenceRoomSeedFields {
  omit_from_directory?: boolean
  moderator_required?: boolean
  moderator_pin?: string | null
  conference_pin_required?: boolean
  conference_pin?: string | null
  participants_join_muted?: boolean
  announce_new_participants?: boolean
  pre_call_announcement?: number | null
  playlist?: number | null
}

/**
 * Call queue-specific fields. PATCH handles name/number/omit;
 * PUT call-queue handles call_queue_members + agent and caller settings.
 */
export interface CallQueueSeedFields {
  omit_from_directory?: boolean
  /** Member lines for display; stored as extension ids. */
  members?: { id: number; number: string; name: string }[]
  /** Agent settings: ring strategy, pre-call announcement, timing. */
  ring_strategy?: string | null
  pre_call_announcement?: number | null
  max_no_answer?: number | null
  busy_delay_time?: number | null
  reject_delay_time?: number | null
  wrap_up_time?: number | null
  no_answer_delay_time?: number | null
  max_ring_duration?: number | null
  /** Caller settings: max callers, hold music, timeouts, announcements. */
  queue_max_callers?: number | null
  playlist_id?: number | null
  max_wait_time?: number | null
  queue_timeout_destination?: number | null
  queue_escape_destination?: number | null
  periodic_announcements_enable?: boolean
  position_announcement_frequency?: number | null
  periodic_announcement_message?: number | null
}

/**
 * Virtual fax-specific fields. PATCH extension handles name/number/omit +
 * receiving_email_destination + secure fax fields; PUT user-groups handles add/remove.
 */
export interface VirtualFaxSeedFields {
  omit_from_directory?: boolean
  receiving_email_destination?: string | null
  allow_message_review?: boolean
  /** Download count (legacy field: say_message_timestamp). */
  secure_fax_download_count?: number | null
  /** Failed login retries (legacy field: escape_to_operator). */
  secure_fax_failed_login_retries?: number | null
  /** Link expiry in minutes (legacy field: say_callerid). */
  secure_fax_link_expiry_minutes?: number | null
  /** Count of undownloaded secure faxes available to resend. */
  resendable_secure_fax_count?: number | null
  /** Assigned user groups for picklist. */
  user_groups?: { id: number; name: string }[]
}

/**
 * Shared voicemail-specific fields. PATCH extension handles name/number/omit +
 * voicemail/options; PUT user-groups handles add/remove.
 */
export interface SharedVoicemailSeedFields {
  omit_from_directory?: boolean
  voicemail_time_zone?: string | null
  voicemail_password?: string | null
  play_voicemail_instructions?: boolean
  send_email_notifications?: boolean
  attach_voicemail_message?: boolean
  delete_message_after_send?: boolean
  message_email_address?: string | null
  use_system_greetings?: boolean
  outgoing_message_unavailable_id?: number | null
  user_groups?: { id: number; name: string }[]
}

/** Union type for extension with all possible type-specific fields. */
export type ExtensionWithTypeFields = ExtensionInstance &
  Partial<LineSeedFields> &
  Partial<RingGroupSeedFields> &
  Partial<CallQueueSeedFields> &
  Partial<ConferenceRoomSeedFields> &
  Partial<VirtualFaxSeedFields> &
  Partial<SharedVoicemailSeedFields>

/** Extension with optional display fields for list views. */
export interface ExtensionForDisplay {
  id: number
  uuid: string
  type: ExtensionType | string
  name: string
  number: string
  created_at: string
  updated_at: string
  /**
   * Active state. The API serializer returns `active` as a boolean (Django
   * `BooleanField`); legacy/internal payloads sometimes used a numeric
   * `is_active` 0/1 flag, which we still tolerate here for safety.
   */
  active?: boolean | number | null
  is_active?: number | boolean | null
  assigned_user?: { id: number; name: string }
  assigned_profile?: { id: number; name: string }
  location?: { id: number; name: string }
  permissions?: {
    can_edit?: boolean
    can_update?: boolean
    can_delete?: boolean
  }
  can_edit?: boolean
  can_update?: boolean
  can_delete?: boolean
}

/** Line extension for display in Lines list view. */
export type LineForDisplay = ExtensionForDisplay

/** Permission flags on an extension. */
export interface ExtensionPermissionFlags {
  permissions?: {
    can_edit?: boolean
    can_update?: boolean
    can_delete?: boolean
  }
  can_edit?: boolean
  can_update?: boolean
  can_delete?: boolean
}

/**
 * Check whether an extension is inactive.
 *
 * The /extensions/ serializer returns `active` as a Django boolean
 * (`true`/`false`). Some legacy responses also surface a numeric `is_active`
 * (0/1). Treat any of those falsy variants as inactive.
 */
export const isInactiveExtension = (ext: ExtensionForDisplay): boolean => {
  if (ext.active === false || ext.active === 0) return true
  if (ext.is_active === false || ext.is_active === 0) return true
  return false
}
